import React from 'react'
import { useTranslation } from 'react-i18next'
import { CheckInMonthData } from './type'
import { useSearchStore } from '../../../store/jpeTopPageStore'
import { formatPrice } from '../utils'
import { CheckInMonthSkeleton } from './CheckInMonthSkeleton'
import { AvailabilityStatusIcon } from '../../availability-status/AvailabilityStatusIcons'
import { AvailabilityStatusLabel } from '../../availability-status/AvailabilityStatusLabel'
import { monthPercentToDisplayKind } from '../../availability-status/statusFromCheckInDate'

interface CheckInMonthProps {
  checkInMonths: CheckInMonthData[]
  nurseryId: string
  availabilityUrl?: string
  loading?: boolean
  error?: boolean
  onRetry?: () => void
}

export const CheckInMonth: React.FC<CheckInMonthProps> = ({
  checkInMonths,
  nurseryId,
  availabilityUrl,
  loading = false,
  error = false,
  onRetry,
}) => {
  const { t } = useTranslation()
  const { selectedCurrency, searchData } = useSearchStore()

  const getEstimateText = () => {
    const adults = searchData.number_of_adults || 2
    const children = searchData.number_of_children || 1
    const adultsText = `${adults} adult${adults > 1 ? 's' : ''}`
    const childrenText = `${children} child${children > 1 ? 'ren' : ''}`

    return `Estimate for ${adultsText} and ${childrenText}`
  }

  const renderPrice = (priceInJPY: number) => {
    const isJPY = selectedCurrency === 'JPY'
    const convertedPrice = formatPrice(priceInJPY, selectedCurrency)

    const priceClass = 'text-gray-900 font-size-12 mt-1'

    if (isJPY) {
      return (
        <span className={`${priceClass} overflow-hidden text-ellipsis whitespace-nowrap w-full block`}>{convertedPrice}</span>
      )
    }

    const jpyPrice = formatPrice(priceInJPY, 'JPY')
    return (
      <span className={`${priceClass} flex flex-col items-center w-full`}>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap w-full block text-center text-gray-900">
          {convertedPrice}
        </span>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap w-full block text-center text-gray-900">
          ({jpyPrice})
        </span>
      </span>
    )
  }

  const formatMonthLabel = (item: CheckInMonthData) => t(`month.abbr_month_names.${item.month}`)

  const renderCheckInStatus = (item: CheckInMonthData) => {
    const kind = monthPercentToDisplayKind(item.percent)

    if (kind === 'sold_out') {
      return (
        <>
          <AvailabilityStatusIcon kind="sold_out" size={18} />
          <span className="text-xs whitespace-nowrap text-gray-600 mt-2">{t('landing_page.availability_status.sold_out')}</span>
        </>
      )
    }

    if (kind === 'unknown') {
      return <AvailabilityStatusIcon kind="unknown" size={18} />
    }

    const showPrice = (kind === 'available' || kind === 'limited') && item.price !== null

    return (
      <>
        <AvailabilityStatusIcon kind={kind} size={18} />
        <AvailabilityStatusLabel
          kind={kind}
          variant={kind === 'limited' || kind === 'contact' ? 'stacked' : 'inline'}
          className="text-green mt-1"
        />
        {showPrice ? renderPrice(item.price!) : <span className="text-gray-500 font-size-12 mt-1">-</span>}
      </>
    )
  }

  const handleCheckInMonthClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.has('maximum_number_of_adults')) {
      const value = searchParams.get('maximum_number_of_adults')
      searchParams.delete('maximum_number_of_adults')
      searchParams.set('number_of_adults', value)
    }

    if (searchParams.has('maximum_number_of_children')) {
      const value = searchParams.get('maximum_number_of_children')
      searchParams.delete('maximum_number_of_children')
      searchParams.set('number_of_study_abroad', value)
    }

    const modifiedSearch = searchParams.toString() ? '?' + searchParams.toString() : ''
    const url = availabilityUrl ? `${availabilityUrl}${modifiedSearch}` : `/all?nursery_id=${nurseryId}`
    window.open(url, '_blank')
  }

  const handleRetryClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRetry?.()
  }

  if (loading) {
    return <CheckInMonthSkeleton />
  }

  if (error) {
    return (
      <div className="pt-2">
        <div
          className="flex items-center justify-center py-4 text-gray-500 text-sm cursor-pointer hover:text-gray-700 transition-colors"
          onClick={handleRetryClick}
        >
          <span>Failed to load</span>
          <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>
      </div>
    )
  }

  if (!checkInMonths || checkInMonths.length === 0) {
    return null
  }

  return (
    <div className="pt-2">
      <div className="flex overflow-auto justify-start gap-1 mb-2">
        {checkInMonths.map((item, index) => {
          const kind = monthPercentToDisplayKind(item.percent)
          const isSoldOut = kind === 'sold_out'

          return (
            <div
              key={index}
              className={`box-border flex flex-1 flex-col items-center rounded-sm border px-1 py-4 text-center ${
                isSoldOut
                  ? 'border-[#DADDE1] bg-gray-100 opacity-80 cursor-not-allowed'
                  : 'border-gray-300 hover:border-gray-400 cursor-pointer transition-colors'
              }`}
              style={{ minWidth: '95px', maxWidth: '95px' }}
              onClick={isSoldOut ? undefined : handleCheckInMonthClick}
              aria-disabled={isSoldOut || undefined}
            >
              <div className="font-size-13 mb-1 text-[#374151]">{formatMonthLabel(item)}</div>
              <div className="flex flex-col items-center justify-center" style={{ minHeight: '48px' }}>
                {renderCheckInStatus(item)}
              </div>
            </div>
          )
        })}
      </div>
      <div className="text-gray-500 text-xs text-right">{getEstimateText()}</div>
    </div>
  )
}
